import { useMachine } from "@xstate/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { Navigate } from "react-router-dom";

import { useGetMe } from "@elation/api-client/src/stitch-web";

import { Actor } from "@src/actor";
import { IS_IN_IFRAME } from "@src/env";
import { authClient, BEARER_TOKEN_KEY } from "@src/features/auth";
import { requestAuthTokenFromParent } from "@src/features/iframe/messages";
import {
  type SessionRecoveryMode,
  sessionRecoveryMachine,
} from "@src/shared/providers/sessionRecoveryMachine";
import { appStore } from "@src/shared/stores/app";
import * as identify from "@src/shared/utils/observability/identify";
import {
  captureException,
  captureMessage,
} from "@src/shared/utils/observability/sentry";

const MAX_RECOVERY_ATTEMPTS = 3;
const COOKIE_RETRY_DELAY_MS = 150;

export const ActorContext = createContext<Actor | null>(null);

interface ActorProviderProps {
  children: ReactNode;
  loginUrl: (searchParams?: URLSearchParams) => string;
}

/**
 * Represents who is acting within the application right now.
 *
 * Resolves once on login and remains stable for the session.
 * Used to attribute actions to the correct user and role for auditing.
 */
export function ActorProvider({ children, loginUrl }: ActorProviderProps) {
  const {
    data: session,
    isPending: isPendingSession,
    error: sessionError,
    refetch: refetchSession,
  } = authClient.useSession();

  const {
    data,
    error: getMeError,
    isLoading: isInitialLoadingMe,
  } = useGetMe({
    query: {
      enabled: session !== null,
      retry: false,
    },
  });

  const practiceTimezone = data?.data?.practice.timezone;

  useEffect(() => {
    if (!practiceTimezone) {
      return;
    }

    appStore.getState().setPracticeTimezone(practiceTimezone);
  }, [practiceTimezone]);

  // Re-attach the authenticated user's identity to the active Datadog RUM
  // session on bootstrap. `trackUserLoggedIn` only runs on explicit login
  // paths, so a page reload that restores an existing session would otherwise
  // leave RUM without `usr.*` — any event emitted before the next login (e.g.
  // the document-upload funnel resuming its localStorage-persisted polling)
  // would then land unattributed.
  const meProfile = data?.data;
  useEffect(() => {
    if (!meProfile) {
      return;
    }

    identify.setUser(meProfile);
  }, [meProfile]);

  // A failing /me with a valid session is a real backend problem that boots an
  // authenticated user back to login. console.error alone is invisible to
  // Sentry (sentry.ts runs with `integrations: []`, no captureConsole), so
  // capture the concrete error object — the most actionable failure this
  // component produces. In an effect so it reports once per error rather than
  // once per render.
  useEffect(() => {
    if (!getMeError) {
      return;
    }

    captureException(getMeError, {
      tags: { route: "actor-provider", operation: "getMe" },
    });
  }, [getMeError]);

  // A session-verification failure (Better Auth's useSession surfacing an
  // error) also boots the user to /login, and is excluded from the generic
  // fallback below — so without this it would only reach the invisible
  // console.error. Capture it for the same reason as getMeError above.
  useEffect(() => {
    if (!sessionError) {
      return;
    }

    captureException(sessionError, {
      tags: { route: "actor-provider", operation: "useSession" },
    });
  }, [sessionError]);

  // Bounded session recovery. When a protected route mounts without a session,
  // we attempt a bounded recovery before treating it as a real logout:
  //
  // - iframe (EHR host): the bearer token in localStorage can go stale
  //   mid-session. Each attempt asks the parent for a fresh token before
  //   re-fetching. Hippo wires this up by passing `onRefreshAuthToken` to
  //   <StitchIframe>. The host can only mint a token for an active tab, so
  //   after the first (auto) attempt, retries fire on tab refocus.
  // - cookie (non-iframe post-signup race, STITCH-426): Better Auth's session
  //   store can briefly lag a just-set cookie, so the first protected route
  //   renders with session === null. Attempts simply re-fetch, on a timer.
  //
  // The attempt counter and in-flight status live in the machine (read via
  // `state` below), so render output never reads a mutable ref. See
  // `sessionRecoveryMachine.ts`.
  const mode: SessionRecoveryMode = IS_IN_IFRAME ? "iframe" : "cookie";

  // The recovery action closes over `refetchSession`, whose identity can change
  // between renders. The machine is created once, so keep the latest closure in
  // a ref (updated in an effect — never written during render) and have the
  // machine call through it.
  const recover = useCallback(async () => {
    if (IS_IN_IFRAME) {
      const { token } = await requestAuthTokenFromParent();
      if (!token) return;
      localStorage.setItem(BEARER_TOKEN_KEY, token);
    }
    await refetchSession();
  }, [refetchSession]);
  const recoverRef = useRef(recover);
  useEffect(() => {
    recoverRef.current = recover;
  }, [recover]);

  const [recoveryState, sendRecovery] = useMachine(sessionRecoveryMachine, {
    input: {
      mode,
      maxAttempts: MAX_RECOVERY_ATTEMPTS,
      retryDelayMs: mode === "iframe" ? 0 : COOKIE_RETRY_DELAY_MS,
      recover: () => recoverRef.current(),
    },
  });

  // Translate React's session presence into machine events. A present session
  // settles recovery; an absent one (re-)arms it.
  useEffect(() => {
    if (isPendingSession) return;
    sendRecovery(
      session ? { type: "SESSION_FOUND" } : { type: "SESSION_ABSENT" },
    );
  }, [session, isPendingSession, sendRecovery]);

  // iframe only: a foregrounded tab is the cue to spend another attempt. The
  // machine ignores RETRY unless it's between attempts and under budget, so the
  // handler can fire unconditionally (this replaces the old in-flight ref guard
  // that prevented focus+visibilitychange from double-firing on one refocus).
  useEffect(() => {
    if (!IS_IN_IFRAME) return;

    const retryOnForeground = () => {
      if (document.visibilityState === "hidden") return;
      sendRecovery({ type: "RETRY" });
    };

    window.addEventListener("focus", retryOnForeground);
    document.addEventListener("visibilitychange", retryOnForeground);

    return () => {
      window.removeEventListener("focus", retryOnForeground);
      document.removeEventListener("visibilitychange", retryOnForeground);
    };
  }, [sendRecovery]);

  // Hold the render while recovery is still in progress or may imminently
  // start, so a transient null session doesn't bounce to /login. `idle` and
  // `settled` are included for the same reason: on the render where the session
  // first reads null (initial mount → `idle`; a mid-session token expiry →
  // `settled`), the effect that dispatches SESSION_ABSENT hasn't run yet, so
  // there's a 1-render gap where excluding them would fall through to the
  // redirect. `failed` is the only state where we give up and let the redirect
  // through.
  // `recoveryState` is render-visible machine state — no ref is read during render.
  const isHoldingForRecovery =
    recoveryState.matches("idle") ||
    recoveryState.matches("settled") ||
    recoveryState.matches("awaitingAttempt") ||
    recoveryState.matches("recovering");

  // True when we've reached the unexplained fallback branch: no session, no
  // concrete error, recovery exhausted. Computed here (before any early return)
  // so the useEffect below — which must be called unconditionally — can read it.
  const isGenericAuthFallback =
    !isPendingSession &&
    !isInitialLoadingMe &&
    !session &&
    !sessionError &&
    !getMeError &&
    !data?.data &&
    !isHoldingForRecovery &&
    IS_IN_IFRAME;

  // Move captureMessage out of the render path so it fires once per failure
  // event, not once per render. The render path may be invoked multiple times
  // before commit under concurrent rendering / React StrictMode, which would
  // produce duplicate Sentry events for a single failure.
  // Deliberately omits detail deps (session, sessionError, etc.) — one capture
  // per fallback event, not one per stale-closure update.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional single-fire on the boolean transition
  useEffect(() => {
    if (!isGenericAuthFallback) return;

    // `isGenericAuthFallback` negates `sessionError`/`getMeError`, so const-alias
    // narrowing types them as `null` inside this guarded effect. The String()
    // casts are only there to capture the live closure values, which can be
    // non-null at runtime even though TS sees `null`.
    captureMessage("[ActorProvider] generic auth fallback", {
      level: "error",
      extra: {
        hasSession: !!session,
        sessionError: String(sessionError ?? ""),
        getMeError: String(getMeError ?? ""),
        hasData: !!data?.data,
        isInIframe: IS_IN_IFRAME,
      },
    });
  }, [isGenericAuthFallback]);

  if (isPendingSession || isInitialLoadingMe) {
    return <></>; // Todo: maybe add loading UI here
  }

  if (!session && !sessionError && !getMeError && isHoldingForRecovery) {
    return <></>;
  }

  if (!session || sessionError || getMeError || !data?.data) {
    const meErrorMessage = getMeError
      ? "Unable to retrieve user profile"
      : undefined;

    const sessionErrorMessage = sessionError?.message
      ? "Unable to verify your session. Please try logging in again."
      : undefined;

    // In iframe mode the only way to land here without a concrete error is
    // that the bounded parent-token retry exhausted itself — i.e., the host
    // EHR couldn't supply a usable token. Give the user a message that
    // matches what actually happened, so the Refresh button on the iframe
    // login page has obvious meaning.
    const iframeNoTokenMessage =
      IS_IN_IFRAME && !session && !sessionError && !getMeError
        ? "We couldn't refresh your session from the host application. Click Refresh to try again."
        : undefined;

    const error =
      sessionErrorMessage ??
      meErrorMessage ??
      iframeNoTokenMessage ??
      // If a session exists but /me did not return data and did not surface a
      // concrete error, keep a visible fallback. A plain no-session web redirect
      // should be silent: it is the normal first-visit path to a protected page.
      (session ? "An unexpected error occurred" : undefined);

    if (error) {
      console.error("[ActorProvider] Auth failed, redirecting to login", {
        hasSession: !!session,
        sessionError: sessionError?.message,
        getMeError: getMeError?.message,
        hasData: !!data?.data,
      });
    }

    return (
      <Navigate
        to={error ? loginUrl(new URLSearchParams({ error })) : loginUrl()}
        replace
      />
    );
  }

  const me = data.data;

  const actor: Actor =
    me.type === "physician"
      ? {
          type: "physician",
          user: me.user,
          practice: me.practice,
          config: me.config,
        }
      : {
          type: "staff",
          user: me.user,
          practice: me.practice,
          config: me.config,
        };

  return (
    <ActorContext.Provider value={actor}>{children}</ActorContext.Provider>
  );
}

/**
 * Returns the current applicationactor. Must be used within an {@link ActorProvider}.
 */
export function useActor(): Actor {
  const actor = useContext(ActorContext);

  if (!actor) {
    throw new Error("useActor must be used within an ActorProvider");
  }

  return actor;
}

/**
 * Returns the current actor narrowed to the given type.
 *
 * Use this in components that are only reachable by a specific actor type.
 *
 * @throws If the current actor's type does not match the expected type.
 */
export function useAssertActor<T extends Actor["type"]>(
  type: T,
): Extract<Actor, { type: T }> {
  const actor = useActor();

  if (actor.type !== type) {
    throw new Error(`Expected actor type "${type}", got "${actor.type}"`);
  }

  return actor as Extract<Actor, { type: T }>;
}
